<script lang="ts" setup>
import { useDetail } from '~/store/detail'
import type { JobItem } from '~/models/Job'

const route = useRoute()
const detail = useDetail()
const { $config } = useNuxtApp()
const jobDetail = computed(() => detail.jobDetail as JobItem)
const jobLocation = computed(() => jobDetail.value.locations || [])
const searchConfig = useSearchApiConfig()

if (isEmpty(useGetSubdomain()))
  navigateTo('', { redirectCode: 301 })

if (!('job_id' in jobDetail.value) || String(jobDetail.value.job_id) !== route.params.id) {
  try {
    const [jobData] = await Promise.all([
      useAsyncData('get_job_detail', () => searchConfig(`/jobs/epicareer_${route.params.id}`), { server: true }),
    ])

    const rawDetail = jobData.data.value
    if (!isEmpty(rawDetail)) {
      const country = ['us', 'uk', 'sg', 'id', 'my', 'ph', 'cn', 'hk']
      const countryCode = isEmpty(rawDetail.locations) ? 'undefined' : (rawDetail.locations[0]?.country_code.toLowerCase() === 'gb' ? 'uk' : rawDetail.locations[0]?.country_code.toLowerCase())
      if (country.includes(countryCode)) {
        // if countrycode included in our locales
        if (countryCode === useGetSubdomain()) {
          // if countrycode is the same as current subdomain, then show the job
          detail.updateSelectedJob(rawDetail as JobItem)
        }
        else {
          // if countrycode different from the subdomain, then redirect it to its own subdomain
          await navigateTo(useSetSubdomain(countryCode) + route.path, { external: true, redirectCode: 301 })
        }
      }
      else {
        // if countrycode outside of our locale, show error page
        showError({ statusCode: 404, statusMessage: 'Page Not Found', fatal: true })
      }
    }
    else {
      // this mean job is expired
      navigateTo(useGenerateSearchUrl(await searchInJsonFiles(route.params?.slug?.toString() || '')), { redirectCode: 301 })
    }
  }
  catch (error) {
    console.error('Error fetching data', error)
    showError({ message: 'Failed to load data', statusCode: 500 })
  }
}

setMeta()
function setMeta() {
  if (jobDetail.value.job_id) {
    const metaTitle = `${jobDetail.value.title} job at ${jobDetail.value.company_name} - Epicareer`
    const metaDescription = `Find ${jobDetail.value.title} job at ${jobDetail.value.company_name} in ${jobLocation.value[0]?.city || jobLocation.value[0]?.address || ''}, ${jobLocation.value[0]?.country || ''} on Epicareer`
    const robotsDefault = ($config.public.ENV === 'development' || $config.public.ENV === 'staging') ? { name: 'robots', content: 'nofollow, noindex' } : { name: 'robots', content: 'index, follow, max-image-preview:large, max-snippet:-1, max-vide-preview:-1' }

    useHead({
      title: metaTitle,
      meta: [
        { property: 'og:title', content: metaTitle },
        { name: 'twitter:title', content: metaTitle },
        { name: 'description', content: metaDescription },
        { property: 'og:description', content: metaDescription },
        { name: 'twitter:description', content: metaDescription },
        robotsDefault,
      ],
      link: [
        { rel: 'canonical', href: useSetSubdomain(useGetSubdomain()) + route.path },
      ],
    })
  }
}

function setSchema(responsibility: string) {
  useSchemaOrg(
    [
      {
        '@context': 'https://schema.org/',
        '@type': 'JobPosting',
        'title': `${jobDetail.value.title}`,
        'description': responsibility,
        'identifier': {
          '@type': 'PropertyValue',
          'name': jobDetail.value.company_name,
        },
        'datePosted': new Date(jobDetail.value.date_published).toISOString(),
        'validThrough': new Date(jobDetail.value.expiry_date).toISOString(),
        'employmentType': jobDetail.value.types,
        'hiringOrganization': {
          '@type': 'Organization',
          'name': jobDetail.value.company_name,
          'logo': jobDetail.value.company_logo,
        },
        'jobLocation': {
          '@type': 'Place',
          'address': {
            '@type': 'PostalAddress',
            'streetAddress': jobLocation.value[0]?.address,
            'addressRegion': jobLocation.value[0]?.city,
            'addressCountry': jobLocation.value[0]?.country_code,
          },
        },
        'baseSalary': {
          '@type': 'MonetaryAmount',
          'currency': jobDetail.value.currency,
          'value': {
            '@type': 'QuantitativeValue',
            'value': parseInt(jobDetail.value.salary_max || jobDetail.value.salary_min).toLocaleString('de-DE', { minimumFractionDigits: 2 }),
            'unitText': useSalaryTypeFormat(jobDetail.value.salary_type).toUpperCase(),
          },
        },
        'url': `${useSetSubdomain(useGetSubdomain())}${route.fullPath}`,
      },
      {
        '@type': 'WebPage',
        '@id': `${useSetSubdomain(useGetSubdomain())}${route.fullPath}#webpage`,
        'description': responsibility,
        'name': `${jobDetail.value.title}`,
        'url': `${useSetSubdomain(useGetSubdomain())}${route.fullPath}`,
        'about': {
          '@id': `${useSetSubdomain(useGetSubdomain())}/#identity`,
        },
        'isPartOf': {
          '@id': `${useSetSubdomain(useGetSubdomain())}/#website`,
        },
        'potentialAction': [
          {
            '@type': 'ReadAction',
            'target': [
            `${useSetSubdomain(useGetSubdomain())}${route.fullPath}`,
            ],
          },
        ],
        'primaryImageOfPage': {
          '@id': `${useSetSubdomain(useGetSubdomain())}/#logo`,
        },
      },
    ],
  )
}

async function searchInJsonFiles(keyword: string): Promise<string | null> {
  // TO RETURN CLOSEST JOB TITLE BASED ON JOB SLUG
  const baseUrl = useSetSubdomain(useGetSubdomain())

  try {
    const indexRes = await fetch(`${baseUrl}/assets/mkrb-category/subtitle/index.json`)
    if (!indexRes.ok)
      throw new Error('Index file not found')

    const indexData = await indexRes.json()
    const files = indexData.files

    const jsonPromises = files.map(file =>
      fetch(`${baseUrl}/assets/mkrb-category/subtitle/${file}`)
        .then(res => res.ok ? res.json() : null)
        .catch(() => null),
    )

    const jsonDataArray = await Promise.all(jsonPromises)

    const cleanedKeyword = keyword.trim().toLowerCase().split('-')
    let bestMatch: string | null = null
    let bestMatchCount = 0

    jsonDataArray.forEach((jsonData) => {
      if (!jsonData)
        return

      jsonData.forEach((item) => {
        if (!item.en || !item.slug)
          return

        const slugWords = item.slug.trim().toLowerCase().split('-')
        const matchCount = slugWords.filter(word => cleanedKeyword.includes(word)).length

        if (matchCount > bestMatchCount) {
          bestMatchCount = matchCount
          bestMatch = item.slug
        }
      })
    })

    return bestMatch
  }
  catch (error) {
    console.error('Error checking JSON files:', error)
    return null
  }
}
</script>

<template>
  <BaseAtomsSkeletonBounded class="!p-0 max-w-6xl <lg:mx-4 mt-6 lg:mt-10 text-gray-900">
    <div class="mb-6 cursor-pointer flex-shrink-0" @click="$router.back()">
      <Icon name="heroicons:arrow-left-solid" class="h-5 w-5" />
    </div>
    <LazyJobsDetail :show-right="true" :item="jobDetail" @set-schema="(data: string) => setSchema(data)" />
  </BaseAtomsSkeletonBounded>
</template>
